import dayjs from "dayjs";
import { MOCK_PURCHASE_ORDERS } from "./mockData";
import {
  ORDER_NUMBER_FORMAT_ERROR,
  ORDER_NUMBER_PATTERN,
  PO_LINE_QTY_MIN,
  PO_LINE_QTY_MIN_ERROR,
  PO_LINE_QTY_MAX,
  PO_LINE_QTY_MAX_DIGITS,
  PO_LINE_UNIT_COST_MAX,
} from "./createConstants";
import { toVariantKey } from "./productPickerKeys";

export const fmtDateInput = (d) => {
  const yr = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const dy = String(d.getDate()).padStart(2, "0");
  return `${yr}-${mo}-${dy}`;
};

export const fmtDateNice = (s) => {
  if (!s) return "";
  const d = new Date(`${s}T00:00:00`);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const nextOrderNumber = () => {
  const seq = (MOCK_PURCHASE_ORDERS?.length || 0) + 24;
  return `MAI-${seq}`;
};

export const defaultDeliveryDate = () => {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return fmtDateInput(d);
};

/** Mirrors AddPo.js stockDate validation (no past dates; optional issued-date floor). */
export const getStockDueDateError = (date, issuedDate = null) => {
  if (!date || !dayjs(date).isValid()) return "";
  const selected = dayjs(date).startOf("day");
  const today = dayjs().startOf("day");
  if (issuedDate && selected.isBefore(dayjs(issuedDate).startOf("day"))) {
    return "Stock Due Date cannot be older than issued date";
  }
  if (selected.isBefore(today)) {
    return "Stock Due Date cannot be older than present date";
  }
  return "";
};

export const formatStockDueForApi = (date) => {
  if (!date) return "";
  const d = dayjs(date);
  return d.isValid() ? d.format("YYYY-MM-DD") : "";
};

export const computeLineTotal = (line) =>
  (Number(line?.qtyOrdered) || 0) * (Number(line?.unitCost) || 0);

export const computeSubtotal = (items) =>
  Object.values(items || {}).reduce((sum, li) => sum + computeLineTotal(li), 0);

export const computeTotalUnits = (items) =>
  Object.values(items || {}).reduce(
    (sum, li) => sum + (Number(li?.qtyOrdered) || 0),
    0
  );

export const computeDiscountAmount = (subtotal, mode, value) => {
  const num = Number(value) || 0;
  if (mode === "pct") {
    return subtotal * (Math.max(0, Math.min(100, num)) / 100);
  }
  return Math.max(0, num);
};

export const getDiscountAmountError = (subtotal, mode, value) => {
  if (mode !== "amt") return "";
  const num = Number(value) || 0;
  if (num > Math.max(0, subtotal)) {
    return "Discount cannot exceed the subtotal of products.";
  }
  return "";
};

export const computeOrderTotal = ({
  subtotal,
  discountAmount,
  shipping,
  importDuty,
}) =>
  Math.max(0, subtotal - discountAmount) +
  Math.max(0, Number(shipping) || 0) +
  Math.max(0, Number(importDuty) || 0);

export const sanitizeDecimal = (raw) => String(raw || "").replace(/[^\d.]/g, "");

export const sanitizeInteger = (raw) => String(raw || "").replace(/[^\d]/g, "");

export const clampPoLineQuantity = (value, min = 0) => {
  const digits = sanitizeInteger(value).slice(0, PO_LINE_QTY_MAX_DIGITS);
  let n = digits === "" ? 0 : Number(digits);
  if (!Number.isFinite(n)) n = 0;
  return Math.min(PO_LINE_QTY_MAX, Math.max(min, n));
};

/** True when the merchant has not typed a quantity yet (scan-add). */
export const isBlankPoQty = (value) => value === "" || value == null;

export const clampPoLineUnitCost = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.min(PO_LINE_UNIT_COST_MAX, Math.round(n * 100) / 100);
};

/** Clamp qty / unit cost on a line item (create, edit, picker). */
export const normalizePoLineItem = (line, { minQty, allowBlankQty = false } = {}) => {
  if (!line || typeof line !== "object") return line;
  const received = Number(line.qtyReceived) || 0;
  const min = minQty != null ? minQty : received;
  const keepBlank =
    allowBlankQty && isBlankPoQty(line.qtyOrdered) && received <= 0;
  return {
    ...line,
    qtyOrdered: keepBlank ? "" : clampPoLineQuantity(line.qtyOrdered, min),
    unitCost: clampPoLineUnitCost(line.unitCost),
  };
};

/**
 * Clamp fields when updating a single line.
 * Default floor is received qty. Fully received lines on a Received or Partial PO
 * pass `floorAtReceived: false` so required qty can drop below the earlier receipt.
 */
export const normalizePoLineItemPatch = (
  patch,
  line = {},
  { floorAtReceived = true, minQty } = {}
) => {
  const next = { ...patch };
  if (patch.qtyOrdered !== undefined) {
    const receivedFloor = floorAtReceived ? Number(line.qtyReceived) || 0 : 0;
    const min = minQty != null ? Math.max(minQty, receivedFloor) : receivedFloor;
    if (isBlankPoQty(patch.qtyOrdered) && min <= 0) {
      next.qtyOrdered = "";
    } else {
      next.qtyOrdered = clampPoLineQuantity(patch.qtyOrdered, min);
    }
  }
  if (patch.unitCost != null) {
    next.unitCost = clampPoLineUnitCost(patch.unitCost);
  }
  return next;
};

export const getPoLineItemLimitError = (lineItems) => {
  const items = lineItems || [];
  const badQty = items.find((li) => (Number(li.qtyOrdered) || 0) > PO_LINE_QTY_MAX);
  if (badQty) {
    return `Quantity cannot exceed ${PO_LINE_QTY_MAX.toLocaleString()}.`;
  }
  const badCost = items.find(
    (li) => (Number(li.unitCost) || 0) > PO_LINE_UNIT_COST_MAX
  );
  if (badCost) {
    return `Cost price cannot exceed ${PO_LINE_UNIT_COST_MAX.toLocaleString(
      "en-US",
      { minimumFractionDigits: 2, maximumFractionDigits: 2 }
    )}.`;
  }
  return "";
};

export const getPoLineItemMinQtyError = (lineItems) => {
  const items = lineItems || [];
  if (!items.length) {
    return "Add at least one product with quantity greater than zero.";
  }
  if (items.some((li) => (Number(li?.qtyOrdered) || 0) < PO_LINE_QTY_MIN)) {
    return PO_LINE_QTY_MIN_ERROR;
  }
  return "";
};

/** Variant keys for line items with qty below {@link PO_LINE_QTY_MIN}. */
export const getPoLineItemQtyErrorKeys = (lineItems) =>
  new Set(
    (lineItems || [])
      .filter(
        (li) =>
          li?.variantKey && (Number(li?.qtyOrdered) || 0) < PO_LINE_QTY_MIN
      )
      .map((li) => li.variantKey)
  );

/** PO numbers: letters and digits only (matches API-generated format e.g. PO0042). */
export const sanitizeOrderNumber = (raw) =>
  String(raw || "").replace(/[^A-Za-z0-9]/g, "");

export const hasInvalidOrderNumberChars = (raw) =>
  /[^A-Za-z0-9]/.test(String(raw || ""));

export const getOrderNumberFormatError = (orderNumber) => {
  const trimmed = sanitizeOrderNumber(orderNumber).trim();
  if (!trimmed) return "";
  return ORDER_NUMBER_PATTERN.test(trimmed) ? "" : ORDER_NUMBER_FORMAT_ERROR;
};

export const formatMoneyForApi = (value) =>
  (Number(value) || 0).toFixed(2);

/** Client validation before save draft (all non-optional fields). */
export const validateCreatePurchaseOrderDraft = ({
  orderNumber,
  stockDueDate,
  orderingFor,
  lineItems,
}) => {
  const errors = {};
  const trimmedPo = sanitizeOrderNumber(orderNumber).trim();

  if (!trimmedPo) {
    errors.orderNumber = "Order number is required.";
  } else {
    const formatError = getOrderNumberFormatError(trimmedPo);
    if (formatError) errors.orderNumber = formatError;
  }
  if (stockDueDate && dayjs(stockDueDate).isValid()) {
    const dueError = getStockDueDateError(
      dayjs(stockDueDate),
      dayjs().format("YYYY-MM-DD")
    );
    if (dueError) errors.stockDueDate = dueError;
  }
  if (!orderingFor) {
    errors.orderingFor = "Location is required.";
  }

  const lineItemError = getPoLineItemMinQtyError(lineItems);
  if (lineItemError) {
    errors.lineItems = lineItemError;
  } else {
    const limitError = getPoLineItemLimitError(lineItems);
    if (limitError) errors.lineItems = limitError;
  }

  const linesWithQty = (lineItems || []).filter(
    (li) => Number(li?.qtyOrdered) >= PO_LINE_QTY_MIN
  );

  return {
    ok: Object.keys(errors).length === 0,
    errors,
    linesWithQty,
  };
};

/** Edit PO — ordered qty must stay >= received; received lines cannot be removed. */
export const validatePurchaseOrderLineItemsForEdit = ({
  lineItems,
  previousLineItems = [],
}) => {
  const items = lineItems || [];
  if (!items.length) {
    return { ok: false, message: "Add at least one line item." };
  }
  if (items.some((li) => !(Number(li?.qtyOrdered) > 0))) {
    return {
      ok: false,
      message: "Set ordered quantity greater than zero for every line item.",
    };
  }

  const limitError = getPoLineItemLimitError(items);
  if (limitError) {
    return { ok: false, message: limitError };
  }

  const belowReceived = items.find((li) => {
    const ordered = Number(li.qtyOrdered) || 0;
    const received = Number(li.qtyReceived) || 0;
    return received > 0 && ordered < received;
  });
  if (belowReceived) {
    const label = belowReceived.label || belowReceived.title || "a product";
    const received = Number(belowReceived.qtyReceived) || 0;
    return {
      ok: false,
      message: `Ordered quantity cannot be less than received quantity (${received}) for ${label}.`,
    };
  }

  const currentKeys = new Set(items.map((li) => String(li.variantKey ?? "")));
  const removedWithReceipt = (previousLineItems || []).filter(
    (li) =>
      !currentKeys.has(String(li.variantKey ?? "")) &&
      Number(li.qtyReceived) > 0
  );
  if (removedWithReceipt.length) {
    return {
      ok: false,
      message:
        "Cannot remove products that have already been partially or fully received.",
    };
  }

  return { ok: true };
};

/** Client validation before save draft via CSV (no manual line items). */
export const validateCreatePurchaseOrderDraftFromCsv = ({
  orderNumber,
  stockDueDate,
  orderingFor,
  csvFile,
}) => {
  const errors = {};
  const trimmedPo = sanitizeOrderNumber(orderNumber).trim();

  if (!trimmedPo) {
    errors.orderNumber = "Order number is required.";
  } else {
    const formatError = getOrderNumberFormatError(trimmedPo);
    if (formatError) errors.orderNumber = formatError;
  }
  if (stockDueDate && dayjs(stockDueDate).isValid()) {
    const dueError = getStockDueDateError(
      dayjs(stockDueDate),
      dayjs().format("YYYY-MM-DD")
    );
    if (dueError) errors.stockDueDate = dueError;
  }
  if (!orderingFor) {
    errors.orderingFor = "Location is required.";
  }
  if (!csvFile) {
    errors.csvFile = "Upload a CSV file before saving.";
  }

  return {
    ok: Object.keys(errors).length === 0,
    errors,
  };
};

const getAutoPoVariantToken = (row) => {
  if (row?.variant_id != null && String(row.variant_id).trim() !== "") {
    return String(row.variant_id);
  }
  if (row?.upc != null && String(row.upc).trim() !== "") {
    return `upc-${String(row.upc).trim()}`;
  }
  return "base";
};

const resolveAutoPoUnitCost = (row) => {
  const preferred = Number(row?.preferd_vendor_cost);
  if (Number.isFinite(preferred) && preferred > 0) return preferred;
  const fallback = Number(row?.costperItem);
  if (Number.isFinite(fallback) && fallback > 0) return fallback;
  return 0;
};

export const buildAutoPoLineLabel = (row) => {
  const title = String(row?.product_title || "").trim() || "Product";
  const variant = String(row?.variant || row?.variant_title || "").trim();
  if (!variant) return title;
  return `${title} / ${variant}`;
};

/** Map legacy `auto_po_list` rows → new PO line items (create flow). */
export const mapAutoPoResultToLineItems = (rows) => {
  const withUpc = (rows || []).filter(
    (row) => row?.upc && String(row.upc).trim() !== ""
  );

  return withUpc.map((row) => {
    const productId = String(row.product_id || "");
    const variantId =
      row.variant_id != null && String(row.variant_id).trim() !== ""
        ? String(row.variant_id)
        : "";
    const variantToken = getAutoPoVariantToken(row);
    const reorderQty = Number(row.reorder_qty);
    const label = buildAutoPoLineLabel(row);

    return normalizePoLineItem(
      {
        productId,
        variantId,
        variantKey: toVariantKey(productId, variantToken),
        sku: row.upc || "",
        upc: row.upc || "",
        title: label,
        label,
        qtyOrdered: reorderQty > 0 ? reorderQty : 1,
        qtyReceived: 0,
        unitCost: resolveAutoPoUnitCost(row),
        price: Number(row.price) || 0,
        currentInventory: Number(row.item_qty) || 0,
      },
      { minQty: 1 }
    );
  });
};
